import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const LEGAL_LINKS = [
  { to: '/aviso-legal', label: 'Aviso legal' },
  { to: '/privacidad', label: 'Política de privacidad' },
  { to: '/cookies', label: 'Política de cookies' },
  { to: '/terminos', label: 'Términos y condiciones' },
  { to: '/desistimiento', label: 'Desistimiento' },
];

const LegalLayout = ({ title, subtitle, lastUpdate, children }) => {
  const { pathname } = useLocation();
  return (
    <div className="pt-28 pb-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a227] font-sans font-semibold mb-3">
            Información legal
          </p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] leading-tight mb-3"
            data-testid={`legal-title-${pathname.replace('/', '')}`}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-base text-[hsl(var(--text-secondary))] leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
          {lastUpdate && (
            <p className="text-xs text-[hsl(var(--text-tertiary))] mt-4 font-sans">
              Última actualización: {lastUpdate}
            </p>
          )}
        </div>

        {/* Sub-nav */}
        <nav className="mb-10 flex flex-wrap gap-x-6 gap-y-2 pb-5 border-b border-[#e8e0d0]">
          {LEGAL_LINKS.map(l => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm transition-colors ${pathname === l.to ? 'text-[#c9a227] font-medium' : 'text-[hsl(var(--text-secondary))] hover:text-[#c9a227]'}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Content */}
        <article className="legal-content space-y-6 text-[15px] text-[hsl(var(--text-secondary))] leading-[1.75]">
          {children}
        </article>

        {/* Footer note */}
        <div className="mt-16 pt-6 border-t border-[#e8e0d0] text-xs text-[hsl(var(--text-tertiary))]">
          <p>Si tienes cualquier duda sobre este texto, escríbenos a{' '}
            <a href="mailto:hola@herminiovazquez.com" className="text-[#c9a227] hover:underline">hola@herminiovazquez.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

/* Helper styled components used in all legal pages */
export const LegalH2 = ({ children, id }) => (
  <h2
    id={id}
    className="text-xl sm:text-2xl font-heading text-[hsl(var(--text-primary))] mt-10 mb-3 scroll-mt-28"
  >
    {children}
  </h2>
);
export const LegalH3 = ({ children }) => (
  <h3 className="text-base font-sans font-semibold text-[hsl(var(--text-primary))] mt-6 mb-2">
    {children}
  </h3>
);
export const LegalP = ({ children }) => (
  <p className="text-[15px] leading-[1.75]">{children}</p>
);
export const LegalUl = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 text-[15px] leading-[1.7]">{children}</ul>
);
export const LegalTable = ({ headers, rows }) => (
  <div className="overflow-x-auto my-4 border border-[#e8e0d0] rounded-lg">
    <table className="w-full text-sm">
      <thead className="bg-[#faf6ed]">
        <tr>
          {headers.map(h => (
            <th key={h} className="text-left px-4 py-3 font-sans font-semibold text-[hsl(var(--text-primary))] border-b border-[#e8e0d0]">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fdfbf5]'}>
            {r.map((c, j) => (
              <td key={j} className="px-4 py-3 border-b border-[#f0ead8] align-top">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default LegalLayout;
